// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api/v1";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("adminToken");
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: getAuthHeaders(),
    ...options,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

// Storytellers API functions
export const storytellersAPI = {
  // Get all storytellers (admin)
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/storytellers${queryString ? `?${queryString}` : ""}`;
    return apiCall(endpoint);
  },

  // Get published storytellers (public)
  getPublished: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/public/storytellers${
      queryString ? `?${queryString}` : ""
    }`;
    return apiCall(endpoint);
  },

  // Get storyteller by ID
  getById: async (id) => {
    return apiCall(`/storytellers/${id}`);
  },

  // Get storyteller by storyteller_id (public)
  getByStorytellerId: async (storytellerId) => {
    return apiCall(`/public/storytellers/${storytellerId}`);
  },

  // Create storyteller
  create: async (data) => {
    return apiCall("/storytellers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Update storyteller
  update: async (id, data) => {
    return apiCall(`/storytellers/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // Delete storyteller
  delete: async (id) => {
    return apiCall(`/storytellers/${id}`, {
      method: "DELETE",
    });
  },

  // Publish storyteller
  publish: async (id) => {
    return apiCall(`/storytellers/${id}/publish`, {
      method: "POST",
    });
  },

  // Unpublish storyteller
  unpublish: async (id) => {
    return apiCall(`/storytellers/${id}/unpublish`, {
      method: "POST",
    });
  },

  // Get storyteller statistics
  getStats: async () => {
    return apiCall("/storytellers/stats");
  },
};

// Storyteller Reservations API functions
export const storytellerReservationsAPI = {
  // Get all reservations (admin)
  getAllReservations: async (params = "") => {
    const endpoint = `/storyteller-reservations${params ? `?${params}` : ""}`;
    return apiCall(endpoint);
  },

  // Get reservation by ID (admin)
  getReservationById: async (id) => {
    const endpoint = `/storyteller-reservations/id/${id}`;
    return apiCall(endpoint);
  },

  // Get reservation by public ID
  getReservationByReservationId: async (reservationId) => {
    const endpoint = `/storyteller-reservations/${reservationId}`;
    return apiCall(endpoint);
  },

  // Create reservation (public)
  createReservation: async (data) => {
    const endpoint = `/storyteller-reservations`;
    return apiCall(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Update reservation (admin)
  updateReservation: async (id, data) => {
    const endpoint = `/storyteller-reservations/${id}`;
    return apiCall(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // Delete reservation (admin)
  deleteReservation: async (id) => {
    const endpoint = `/storyteller-reservations/${id}`;
    return apiCall(endpoint, {
      method: "DELETE",
    });
  },

  // Update reservation status (admin)
  updateReservationStatus: async (id, data) => {
    const endpoint = `/storyteller-reservations/${id}/status`;
    return apiCall(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  // Get reservation stats (admin)
  getReservationStats: async () => {
    const endpoint = `/storyteller-reservations/stats`;
    return apiCall(endpoint);
  },
};

// Generic API functions for other resources
export const api = {
  // Generic GET request
  get: async (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const fullEndpoint = `${endpoint}${queryString ? `?${queryString}` : ""}`;
    return apiCall(fullEndpoint);
  },

  // Generic POST request
  post: async (endpoint, data) => {
    return apiCall(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Generic PUT request
  put: async (endpoint, data) => {
    return apiCall(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // Generic DELETE request
  delete: async (endpoint) => {
    return apiCall(endpoint, {
      method: "DELETE",
    });
  },

  // Upload file
  upload: async (endpoint, formData) => {
    const token = localStorage.getItem("adminToken");
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  },
};

// Upload file function
export const uploadFile = async (endpoint, formData) => {
  const token = localStorage.getItem("adminToken");
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    );
  }

  return await response.json();
};

const apiService = {
  storytellersAPI,
  api,
  uploadFile,
  API_BASE_URL,
  API_URL,
};

export default apiService;
